import React, { memo } from 'react'
import { Box, Flex, TextV2 as Text } from '@pinchenterprisesnpm/friday-ui'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle, faCircleDot } from '@fortawesome/pro-regular-svg-icons'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner/info-banner'

const OptionContainer = styled(Flex)<{ selected: boolean; disabled?: boolean }>`
  flex: 1;
  border: 1px solid
    ${({ selected, theme: { palette } }) =>
      selected ? palette.primary : palette.border};
  border-radius: 3px;
  padding: ${({ theme: { mp } }) => mp.sm};
  margin-right: ${({ theme: { mp } }) => mp.sm};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: border 0.2s, background 0.2s;
  background-color: ${({ selected, theme: { palette } }) =>
    selected ? palette.primary + '10' : 'transparent'};

  &:hover {
    border: 1px solid ${({ theme: { palette } }) => palette.primary};
    background-color: ${({ selected, theme: { palette } }) =>
      selected ? palette.primary + '10' : 'transparent'};
  }

  &:last-child {
    margin-right: 0;
  }
`

const IconContainer = styled.div<{ selected: boolean }>`
  color: ${({ selected, theme: { palette } }) =>
    selected ? palette.primary_600 : palette.muted};
  margin-right: ${({ theme: { mp } }) => mp.xs};
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)<{ selected: boolean }>`
  color: ${({ selected, theme: { palette } }) =>
    selected ? palette.primary_600 : 'inherit'};
  font-weight: ${({ selected, theme: { font } }) =>
    selected ? font.weights.semiBold : font.weights.regular};
  font-size: ${({ selected, theme: { font } }) =>
    selected ? font.sizes.h5 : font.sizes.h5};
`

const StyledSubText = styled(Text)<{ selected: boolean }>`
  color: ${({ selected, theme: { palette } }) =>
    selected ? palette.primary_600 : palette.muted};
`

const TextContainer = styled(Flex)`
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
`

export interface IAutoAcceptTypeSelectorProps {
  value: boolean
  onValueChange: (value: boolean) => void
  disabled?: boolean
  containerProps?: any
}

const AutoAcceptTypeSelector = memo(
  ({
    value,
    onValueChange,
    disabled = false,
    containerProps,
  }: IAutoAcceptTypeSelectorProps) => {
    const handleOptionPress = (optionValue: boolean) => {
      if (!disabled) {
        onValueChange?.(optionValue)
      }
    }

    return (
      <Flex
        flexDirection="column"
        {...containerProps}
        onClick={e => e.stopPropagation()}
      >
        <Box mb="xs" mt="xs">
          <InfoBanner
            description="Please select one of the two options below to proceed. This selection determines how your shift will be filled if roster Upshifters are unavailable."
            variant="primary"
            marginTop="-1em"
            textVariant="xsmall"
          />
        </Box>
        <Flex flexDirection="row">
          {/* Left Option - Let other verified Upshifters fill */}
          <OptionContainer
            selected={value === true}
            disabled={disabled}
            onClick={() => handleOptionPress(true)}
          >
            <Flex alignItems="flex-start">
              <IconContainer selected={value === true}>
                <FontAwesomeIcon
                  icon={value === true ? faCircleDot : faCircle}
                  size="sm"
                />
              </IconContainer>
              <TextContainer>
                <StyledText variant="small" selected={value === true}>
                  Let any verified Upshifter claim this shift if my roster
                  cannot fill it.
                </StyledText>
                <StyledSubText
                  variant="xsmall"
                  selected={value === true}
                  mt="xs"
                >
                  Recommended for maximizing your fill rate.
                </StyledSubText>
              </TextContainer>
            </Flex>
          </OptionContainer>

          {/* Right Option - Don't fill if rosters not available */}
          <OptionContainer
            selected={value === false}
            disabled={disabled}
            onClick={() => handleOptionPress(false)}
          >
            <Flex alignItems="flex-start">
              <IconContainer selected={value === false}>
                <FontAwesomeIcon
                  icon={value === false ? faCircleDot : faCircle}
                  size="sm"
                />
              </IconContainer>
              <TextContainer>
                <StyledText variant="small" selected={value === false}>
                  Review applicants if my roster cannot fill the shift.
                </StyledText>
                <StyledSubText
                  variant="xsmall"
                  selected={value === false}
                  mt="xs"
                >
                  Others can still apply — you stay in control of who gets
                  accepted.
                </StyledSubText>
              </TextContainer>
            </Flex>
          </OptionContainer>
        </Flex>
      </Flex>
    )
  },
)

AutoAcceptTypeSelector.displayName = 'AutoAcceptTypeSelector'

export default AutoAcceptTypeSelector
